import {
  ICombinedSimpleModel,
  ISimpleFilterModel,
  NumberFilterModel,
} from '@ag-grid-community/core'
import { splitData } from '@helios/utils'
import { FilterType, MultiFilter, SingleFilter } from './filter'

export interface NumberFilter extends MultiFilter<number> {
  filterType: FilterType.Number
  filters: SingleNumberFilter[]
}

type NumberFilterOperator =
  | 'equals'
  | 'notEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'inRange'
  | 'in'
  | 'notIn'
  | 'blank'
  | 'notBlank'
  | 'equalsAbsolute'

/*
Either value, values, or range must be present. Value must be present on operations expecting only one value.
And values must be present on operators where multiple values could be present like in and not in.
 */
interface SingleNumberFilter extends SingleFilter<number> {
  operator: NumberFilterOperator

  // filter based on absolute value of the arguments
  abs?: boolean
  value?: number

  // populated if the operator is 'in' or 'notIn'
  values?: number[]

  // populated if the operator is 'inRange'
  range?: Range
}

interface Range {
  from: number
  to: number
}

/**
 *
 */
function isNumberFilterOperator(s: string): s is NumberFilterOperator {
  return [
    'equals',
    'notEqual',
    'lessThan',
    'lessThanOrEqual',
    'greaterThan',
    'greaterThanOrEqual',
    'inRange',
    'in',
    'notIn',
    'blank',
    'notBlank',
    'equalsAbsolute',
  ].includes(s)
}

/**
 *
 */
export function convertICombinedSimpleModelToNumberFilter(
  combinedFilter: ICombinedSimpleModel<ISimpleFilterModel>,
): NumberFilter | null {
  if (
    combinedFilter.condition1.filterType !== FilterType.Number ||
    combinedFilter.condition2.filterType !== FilterType.Number
  ) {
    console.error('Filter type should be number', combinedFilter)
    return null
  }
  const firstFilterModel = combinedFilter.condition1 as NumberFilterModel
  const firstSingleFilter = convertSingleFilter(firstFilterModel)

  const secondFilterModel = combinedFilter.condition2 as NumberFilterModel
  const secondSingleFilter = convertSingleFilter(secondFilterModel)

  if (firstSingleFilter && secondSingleFilter) {
    return {
      operator: combinedFilter.operator,
      filterType: FilterType.Number,
      filters: [firstSingleFilter, secondSingleFilter],
    }
  } else {
    return null
  }
}

/**
 *
 */
export function convertISimpleFilterModelToNumberFilter(
  filter: ISimpleFilterModel,
): NumberFilter | null {
  if (filter.filterType !== FilterType.Number) {
    console.error('Filter type should be number', filter)
    return null
  }
  const filterModel = filter as NumberFilterModel
  const singleFilter = convertSingleFilter(filterModel)
  if (singleFilter) {
    return {
      operator: 'AND',
      filterType: FilterType.Number,
      filters: [singleFilter],
    }
  } else {
    return null
  }
}

/**
 *
 */
function convertSingleFilter(filterModel: NumberFilterModel) {
  if (filterModel.type && isNumberFilterOperator(filterModel.type)) {
    const singleNumberFilter: SingleNumberFilter = {
      operator: filterModel.type,
    }

    const type = filterModel.type as unknown

    if (type === 'in' || type === 'notIn')
      singleNumberFilter.values = splitData(
        filterModel?.filter?.toString(),
      ).map(s => Number.parseFloat(s))
    // `!= null`, not truthiness: 0 is a real bound. A truthy check dropped "greaterThan 0" (and an
    // inRange starting at 0) as "no value", silently un-filtering the column.
    else if (
      filterModel.type === 'inRange' &&
      filterModel.filter != null &&
      filterModel.filterTo != null
    ) {
      singleNumberFilter.range = {
        from: filterModel.filter,
        to: filterModel.filterTo,
      }
    } else if (filterModel.filter != null) {
      singleNumberFilter.value = filterModel.filter
    } else if (type === 'blank' || type === 'notBlank') {
      return singleNumberFilter
    } else {
      console.error('Filter value(s) not supported', filterModel)
      return null
    }
    return singleNumberFilter
  } else {
    console.error('Filter operator not supported', filterModel)
    return null
  }
}
